import { FloppyDiskIcon } from '@phosphor-icons/react';
import { FC, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { PageHeader } from '@/modules/common';
import LoadingContainer from '@/modules/common/components/loading-container';
import { Routes } from '@/router/routes';

import { useModelSizeDetails } from '../api';
import { ModelSizeForm } from '../components';
import { ModelSizeFormValues } from '../validations';

export const ModelSizeDetailsPage: FC = () => {
  const params = useParams<{ id: string }>();

  const { details, isFetching, updateModelSizeMutation } = useModelSizeDetails(
    params.id
  );

  const defaultValues = useMemo<ModelSizeFormValues | undefined>(() => {
    if (!details) return undefined;

    return {
      value: details.value,
      sortOrder: details.sortOrder,
      hints: details.hints.map((hint) => ({
        id: hint.id,
        value: hint.value,
        measure: hint.measure,
        sortOrder: hint.sortOrder,
      })),
    };
  }, [details]);

  const updateModelSize = async (data: ModelSizeFormValues) => {
    try {
      await updateModelSizeMutation.mutateAsync({
        id: params.id,
        value: data.value,
        sortOrder: data.sortOrder,
        hints: data.hints.map((hint) => ({
          ...(hint.id && { id: hint.id }),
          value: hint.value,
          measure: hint.measure,
          sortOrder: hint.sortOrder,
        })),
      });
      toast.success(`Model size “${data.value}” updated successfully!`);
    } catch (error) {
      console.error(error.message);
    }
  };

  return (
    <LoadingContainer loading={isFetching}>
      <ModelSizeForm onSubmit={updateModelSize} defaultValues={defaultValues}>
        <PageHeader
          title={details?.value}
          asChildPage
          backPath={Routes.ModelSizes}
          actions={[
            <Button
              key="save-model-size"
              type="submit"
              data-testid="save-model-size-button"
              isLoading={updateModelSizeMutation.isPending}
              startIcon={<FloppyDiskIcon />}
            >
              Save Changes
            </Button>,
          ]}
        />
      </ModelSizeForm>
    </LoadingContainer>
  );
};
